import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isSharedEmailDomain,
  type PlatformOverview,
  type WaitlistRequest,
  type WaitlistStatus,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingPane,
  PageHeader,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { MiniBars } from './MiniBars.js';
import './platform.css';

function gbp(pence: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(
    pence / 100,
  );
}

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

export function PlatformOverviewPage() {
  const { me } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isSystemAdmin = me?.user.isSystemAdmin ?? false;
  const [waitlistFilter, setWaitlistFilter] = useState<WaitlistStatus | ''>('pending');

  const overview = useQuery({
    queryKey: ['platform-overview'],
    queryFn: () => apiFetch<PlatformOverview>('/admin/overview'),
    enabled: isSystemAdmin,
  });
  const waitlist = useQuery({
    queryKey: ['admin-waitlist', waitlistFilter],
    queryFn: () =>
      apiFetch<{ items: WaitlistRequest[] }>('/admin/waitlist', {
        query: { status: waitlistFilter || undefined },
      }),
    enabled: isSystemAdmin,
  });

  const decideWaitlist = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approve' | 'reject' }) =>
      apiFetch<WaitlistRequest>(`/admin/waitlist/${id}/${decision}`, { method: 'POST' }),
    onSuccess: (updated) => {
      toast(
        updated.status === 'approved'
          ? `${updated.domain} approved — everyone at that domain can now sign in.`
          : `${updated.companyName} rejected.`,
        'success',
      );
      void queryClient.invalidateQueries({ queryKey: ['admin-waitlist'] });
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not update the request', 'error'),
  });

  const syncTemplates = useMutation({
    mutationFn: () => apiFetch<{ synced: number }>('/ai/templates/sync', { method: 'POST' }),
    onSuccess: (r) => toast(`Synced ${r.synced} AI prompt templates.`, 'success'),
    onError: (err) => toast(err instanceof ApiRequestError ? err.message : 'Sync failed', 'error'),
  });

  if (!isSystemAdmin) {
    return (
      <EmptyState
        title="System administrators only"
        message="This console manages platform-level settings, standards and operational data."
      />
    );
  }
  if (overview.isLoading) return <LoadingPane label="Loading platform overview" />;
  const o = overview.data;

  return (
    <div>
      <PageHeader
        title="Platform overview"
        subtitle="Recurring revenue, usage and activity across every organisation"
        actions={
          <Button variant="secondary" loading={syncTemplates.isPending} onClick={() => syncTemplates.mutate()}>
            Sync AI templates
          </Button>
        }
      />

      {o && (
        <>
          <div className="plat-tiles">
            <div className="plat-tile accent">
              <div className="value">{gbp(o.revenue.mrrPence)}</div>
              <div className="label">Est. monthly recurring revenue</div>
              <div className="sub">
                {o.revenue.paidOrgs} paid × {gbp(o.revenue.monthlyPricePence)} · {gbp(o.revenue.arrPence)} ARR
              </div>
            </div>
            <div className="plat-tile">
              <div className="value">{o.totals.orgs.toLocaleString()}</div>
              <div className="label">Organisations</div>
              <div className="sub">
                {o.totals.activeOrgs} active · {o.totals.paidOrgs} paid
              </div>
            </div>
            <div className="plat-tile">
              <div className="value">{o.totals.users.toLocaleString()}</div>
              <div className="label">Users</div>
            </div>
            <div className="plat-tile">
              <div className="value">{o.usage.openaiCallsTotal.toLocaleString()}</div>
              <div className="label">OpenAI calls</div>
              <div className="sub">{o.usage.openaiCalls30d.toLocaleString()} in the last 30 days</div>
            </div>
            <div className="plat-tile">
              <div className="value">{bytes(o.usage.storageBytes)}</div>
              <div className="label">Data stored</div>
              <div className="sub">
                {bytes(o.usage.evidenceBytes)} evidence · {bytes(o.usage.importBytes)} imports
              </div>
            </div>
            <div className="plat-tile">
              <div className="value">{o.totals.assessments.toLocaleString()}</div>
              <div className="label">Assessments</div>
              <div className="sub">{o.totals.exports.toLocaleString()} exports generated</div>
            </div>
          </div>

          <div className="plat-charts">
            <Card title="New organisations / week">
              <MiniBars data={o.charts.signupsPerWeek} />
            </Card>
            <Card title="AI calls / week">
              <MiniBars data={o.charts.aiCallsPerWeek} />
            </Card>
            <Card title="AI usage by type">
              {o.usage.aiJobsByKind.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No AI jobs yet.</p>
              ) : (
                <ul className="plat-kinds">
                  {o.usage.aiJobsByKind.map((k) => {
                    const max = Math.max(1, ...o.usage.aiJobsByKind.map((x) => x.count));
                    return (
                      <li key={k.kind}>
                        <span>{k.kind}</span>
                        <span className="kind-bar">
                          <span style={{ width: `${Math.round((k.count / max) * 100)}%` }} />
                        </span>
                        <strong>{k.count}</strong>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}

      <Card
        title="Waiting list"
        actions={
          <select
            className="select"
            style={{ width: 'auto' }}
            aria-label="Filter waiting-list requests"
            value={waitlistFilter}
            onChange={(e) => setWaitlistFilter(e.target.value as WaitlistStatus | '')}
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        }
      >
        {(waitlist.data?.items.length ?? 0) === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
            {waitlistFilter === 'pending'
              ? 'No requests waiting — new submissions from the public form appear here.'
              : 'Nothing here yet.'}
          </p>
        ) : (
          <Table
            head={
              <>
                <th>Requested</th>
                <th>Name</th>
                <th>Company</th>
                <th>Domain</th>
                <th>Contact</th>
                <th>Status</th>
                <th />
              </>
            }
          >
            {waitlist.data!.items.map((r) => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</td>
                <td>{r.name}</td>
                <td>{r.companyName}</td>
                <td>
                  <Badge tone="info">{r.domain}</Badge>
                  {isSharedEmailDomain(r.domain) && (
                    <div style={{ color: 'var(--color-warning)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
                      ⚠ shared provider — approving admits <em>anyone</em> at this domain
                    </div>
                  )}
                </td>
                <td>
                  {r.email}
                  {r.phone && (
                    <div style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>{r.phone}</div>
                  )}
                </td>
                <td>
                  <Badge
                    tone={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'neutral' : 'warning'}
                  >
                    {r.status}
                  </Badge>
                </td>
                <td>
                  {r.status === 'pending' ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        size="sm"
                        loading={decideWaitlist.isPending}
                        onClick={() => {
                          if (
                            isSharedEmailDomain(r.domain) &&
                            !window.confirm(
                              `${r.domain} is a shared email provider. Approving it lets ANYONE with a ${r.domain} address sign in — not just ${r.companyName}. Approve anyway?`,
                            )
                          ) {
                            return;
                          }
                          decideWaitlist.mutate({ id: r.id, decision: 'approve' });
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={decideWaitlist.isPending}
                        onClick={() => decideWaitlist.mutate({ id: r.id, decision: 'reject' })}
                      >
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                      {r.reviewedByName ?? ''}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="Manage">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link to="/admin/orgs">
            <Button variant="secondary">Manage organisations</Button>
          </Link>
          <Link to="/admin/standards">
            <Button variant="secondary">Manage standards</Button>
          </Link>
          <Link to="/admin/audit">
            <Button variant="secondary">Cross-tenant audit log</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
